/* eslint-disable no-param-reassign */

import React, { useState, useEffect, useCallback } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ListGroup, Modal, ModalHeader, ModalFooter } from 'reactstrap';
import { ListGroupItem, Button } from '@cordata/buffet';
import { all } from 'axios';
import { parse } from 'date-fns';
import { capitalize, debounce } from 'lodash';

import useCit from '~/utils/cit/hook';

import PatientSummary from './PatientSummary';
import SubmissionSummary from './SubmissionSummary';

const PatientLookup = () => {
  const toastId = 'check-this';
  const {
    formData,
    isDataLoaded,
    actions: { patientDuplicateCheck, getPreviousSubmissions },
  } = useCit();
  const [duplicates, setDuplicates] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const {
    values: {
      firstName, lastName, genderUid, birthDate, addr1, priorityNote,
    },
    setFieldValue,
  } = useFormikContext();

  const [t] = useTranslation();
  const [selectedPatient, setSelectedPatient] = useState();

  const debouncedDupeCheck = useCallback(
    debounce(props => {
      patientDuplicateCheck(props).then(r =>
        all(r.map(rr =>
          getPreviousSubmissions(rr.summaryPatient?.personUid).then(submissions => {
            rr.previousSubmissions = submissions;
          })))
          .then(() => {
            if (r?.length) {
              setDuplicates(r);

              toast.update(toastId, {
                type: toast.TYPE.WARNING,
                render: 'Potential Matches Found',
                autoClose: true,
              });
              setIsOpen(true);
            } else {
              toast.update(toastId, {
                type: toast.TYPE.INFO,
                render: 'No Matches Found',
                autoClose: true,
              });
            }
          })
          .catch(e =>
            toast.update(toastId, {
              type: toast.TYPE.ERROR,
              render: e.message,
              autoClose: true,
            })));
    }, 1000),
    [],
  );

  const [justSelectedDupe, setJustSelectedDupe] = useState(false);

  useEffect(() => {
    if (justSelectedDupe) {
      // This is to prevent a continuous cycle of:
      //  1. Check for dupes
      //  2. Select a dupe
      //  3. Set the properties from the dupe on the form
      //  4. Check for dupes again because birthDate and genderUid were just set
      setJustSelectedDupe(false);
    } else if (isDataLoaded && firstName && lastName && genderUid) {
      toast('Checking for duplicates ...', { autoClose: false, toastId });

      debouncedDupeCheck({
        firstName,
        lastName,
        genderLabel: genderUid.genderLabel,
        birthDate,
      });
    }
  }, [firstName, lastName, birthDate, genderUid]);

  const selectDuplicate = d => {
    setJustSelectedDupe(true);

    setSelectedPatient();

    setFieldValue('patient', d);

    const primaryAddress = d.addresses?.find(a => a.isPrimary);

    if (primaryAddress && !addr1) {
      setFieldValue('addr1', primaryAddress.addr1 || '');
      setFieldValue('addr2', primaryAddress.addr2 || '');
      setFieldValue('city', primaryAddress.city);
      setFieldValue(
        'stateUid',
        formData.states.find(s => s.divisionAbbrevIso === primaryAddress.provinceIso),
      );
      setFieldValue('postCode', primaryAddress.postCode);
      setFieldValue('county', primaryAddress.county);
    }

    if (d.summaryPatient.birthDate) {
      setFieldValue('birthDate', parse(d.summaryPatient.birthDate));
    }

    setFieldValue(
      'genderUid',
      formData.genders.find(g => g.id === d.gender.genderUid),
    );
    if (d.race) {
      const existingRaceUids = d.race.map(i => i.raceUid);
      setFieldValue(
        'raceUid',
        formData?.races?.filter(i => existingRaceUids.includes(i.id)),
      );
    }

    setFieldValue('phoneNumber', d.preferredPhone.phoneNumber);
    setFieldValue('phoneType', d.preferredPhone.phoneType);

    if (d.summaryPatient.note && !priorityNote) {
      setFieldValue('priorityNote', d.summaryPatient.note);
    }

    const existingVeteranStatus = formData.veteranStatuses.find(vs =>
      d.summaryPatient.badges?.includes(vs.label));

    if (existingVeteranStatus) {
      setFieldValue('veteranStatus', existingVeteranStatus);
    }

    setIsOpen(false);
  };

  const toggleModal = () => setIsOpen(o => !o);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggleModal}
      backdrop="static"
      size={selectedPatient && 'xl'}
    >
      <ModalHeader>
        Potential {capitalize(t('patient'))} Match
        <h6 className="mt-2 text-muted">
          Click on an individual to see more details
        </h6>
      </ModalHeader>

      {selectedPatient ? (
        <ListGroup>
          <Button
            className="rounded-0"
            color="primary"
            icon="chevron-left"
            onClick={() => setSelectedPatient()}
          >
            Back to List
          </Button>

          <PatientSummary patient={selectedPatient} />

          {selectedPatient.previousSubmissions.length > 0 ? (
            <>
              {selectedPatient.previousSubmissions.map(submission => (
                <SubmissionSummary
                  key={submission.id}
                  submission={submission}
                />
              ))}
            </>
          ) : (
            <ListGroupItem>
              <span className="text-muted">
                This {t('patient')} has no prior submissions.
              </span>
            </ListGroupItem>
          )}
        </ListGroup>
      ) : (
        <ListGroup>
          {duplicates.length &&
            duplicates.map(d => (
              <PatientSummary
                key={d.summaryPatient.patientUid}
                action
                patient={d}
                onClick={() => setSelectedPatient(d)}
              />
            ))}
        </ListGroup>
      )}

      <ModalFooter>
        {selectedPatient && (
          <div className="mr-auto">
            <Button
              color="secondary"
              icon="check"
              onClick={() => selectDuplicate(selectedPatient)}
            >
              Select this {t('patient')}
            </Button>
          </div>
        )}

        <Button type="cancel" onClick={() => setIsOpen(false)}>
          Not Listed
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PatientLookup;
